import { useState } from 'react';

import { CountPill, HorizontalMenuTabs } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { type ToolUsageResponse } from '@backrs_ui/grow-ui/src/redux/coachingAnalytics';

import { Box, type SxProps, type Theme, Typography } from '@mui/material';
import { type SystemStyleObject } from '@mui/system';

import { toolLabel } from './analytics.labels';
import { mutedTextSx, selectableRowSx } from './analytics.styles';
import { AnalyticsCard } from './AnalyticsCard';
import { useAgentContext } from './ToolUsageCards.agentContext';
import { useStudentData } from './useStudentData';
import { COLUMNS, COMMON_COLUMNS } from './viewData.columns';

const { toolUsage } = appStrings.modules.coachingAnalytics;

const TOOL_COLUMNS = [
  ...COMMON_COLUMNS,
  COLUMNS.toolsUsed,
  COLUMNS.toolSessions,
  COLUMNS.lastToolSession,
];

const MEASURES = [
  { key: 'unique_students', label: toolUsage.uniqueStudents },
  { key: 'total_sessions', label: toolUsage.totalSessions },
] as const;

export const ToolUsageCard = ({ data }: Readonly<{ data: ToolUsageResponse }>) => {
  useAgentContext(data);

  const [measureIndex, setMeasureIndex] = useState(0);

  const measure = MEASURES[measureIndex].key;
  const tools = [...data.tools].sort((a, b) => b[measure] - a[measure]);
  const max = Math.max(...tools.map((tool) => tool[measure]), 0);

  const students = useStudentData(
    'coaching-analytics-card-tool-usage',
    toolUsage.title,
    TOOL_COLUMNS,
  );

  return (
    <AnalyticsCard
      title={toolUsage.title}
      subtitle={toolUsage.students(data.students.with_any_tool, data.students.total)}
      info={[toolUsage.info]}
      viewData={students}
      belowHeader={
        <Box sx={tabsRowSx}>
          <HorizontalMenuTabs
            id="coaching-analytics-tool-usage-measure-tabs"
            variant="filter"
            tabs={MEASURES.map((option) => option.label)}
            value={measureIndex}
            setValue={setMeasureIndex}
          />
        </Box>
      }
    >
      {tools.length === 0 ? (
        <Typography variant="text-sm-regular" sx={mutedTextSx}>
          {toolUsage.empty}
        </Typography>
      ) : (
        <Box sx={toolListSx}>
          {tools.map((tool) => (
            <Box
              key={tool.tool}
              id={`coaching-analytics-tool-row-${tool.tool}`}
              sx={[toolRowSx, selectableRowSx]}
              onClick={() =>
                students.openSegment({
                  label: toolLabel(tool.tool),
                  where: [{ field: 'tools_used', op: 'eq', value: tool.tool }],
                })
              }
            >
              <Typography variant="text-sm-medium" sx={toolLabelSx}>
                {toolLabel(tool.tool)}
              </Typography>
              <Box sx={toolBarRowSx}>
                <Box sx={toolTrackSx}>
                  <Box
                    sx={toolFillSx}
                    style={{ width: `${max > 0 ? (tool[measure] / max) * 100 : 0}%` }}
                  />
                </Box>
                <CountPill value={tool[measure]} />
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </AnalyticsCard>
  );
};

const tabsRowSx: SxProps<Theme> = {
  display: 'flex',
  width: '100%',
};

// The card is half-width on wide pages, so the rows measure this list rather than the page.
const toolListSx: SxProps<Theme> = ({ backrs }) => ({
  containerType: 'inline-size',
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
});

const toolRowSx = ({ backrs }: Theme): SystemStyleObject<Theme> => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-xs'],
  width: '100%',
  '@container (min-width: 420px)': {
    flexDirection: 'row',
    alignItems: 'center',
    gap: backrs.spacing['spacing-md'],
  },
});

const toolLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  '@container (min-width: 420px)': {
    minWidth: 176,
    flexShrink: 0,
  },
});

const toolBarRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
  minWidth: 0,
  '@container (min-width: 420px)': {
    flex: 1,
  },
});

const toolTrackSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flex: 1,
  height: 24,
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  backgroundColor: backrs.colors['Blue/50'],
  overflow: 'hidden',
});

const toolFillSx: SxProps<Theme> = ({ backrs }) => ({
  height: '100%',
  backgroundColor: backrs.colors['Blue/800'],
});
